// Libs
import classNames from 'classnames/bind';
import { Avatar, Box, Button, FormHelperText, InputLabel, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { IFileUpload } from '~/utils/interface/common';
import {
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_TYPE,
  FILE_UPLOAD_PROP_MAX_FILE,
  FILE_UPLOAD_PROP_MIN_FILE,
  FILE_UPLOAD_PROP_MULTIPLE,
  FILE_UPLOAD_PROP_REQUIRED,
  FILE_UPLOAD_TYPE_STARTS_WITH_IMAGE,
  REG_EXP,
} from '~/utils/constants/common';
import { generateUniqueId } from '~/utils/helper/common';
import { TAcceptFileType } from '~/utils/type/common';
import { FileTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './FileUpload.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  label?: string;
  name?: string;
  accept?: TAcceptFileType;
  showPreview?: boolean;
  uploadedFiles?: string | T[];
  multiple?: boolean;
  maxFiles?: number;
  errorMessage?: string;
  width?: number | string;
  height?: number | string;
  required?: boolean;
  onChange?: (files: T[]) => void;
};

const cx = classNames.bind(styles);

const FileUpload = <T extends IFileUpload>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    label,
    name,
    accept = FILE_UPLOAD_DEFAULT_ACCEPT_TYPE,
    showPreview = true,
    uploadedFiles,
    multiple = FILE_UPLOAD_PROP_MULTIPLE,
    maxFiles = multiple ? FILE_UPLOAD_PROP_MAX_FILE : FILE_UPLOAD_PROP_MIN_FILE,
    errorMessage,
    width,
    height,
    required = FILE_UPLOAD_PROP_REQUIRED,
    onChange,
    ...restProps
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const fileUploadAreaRef = useRef<HTMLButtonElement | null>(null);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [uploadFiles, setUploadFiles] = useState<T[]>([]);
  const [selectedFile, setSelectedFile] = useState<number | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (uploadedFiles && !Array.isArray(uploadedFiles)) {
      setUploadFiles([{ url: uploadedFiles }] as T[]);
    }

    if (Array.isArray(uploadedFiles) && uploadedFiles?.length > DEFAULT_NUMBER_ZERO) {
      setUploadFiles(uploadedFiles as T[]);
    }
  }, [uploadedFiles]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUpload = (index?: number) => {
    // if (uploadFiles?.length >= maxFiles) return;

    if (index !== null && index !== undefined && index >= DEFAULT_NUMBER_ZERO) {
      setSelectedFile(index);
    }

    document.getElementById(name || 'fileUpload')?.click();
  };

  const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files) return;

    const newFiles = Array.from(files);
    const filteredFiles: T[] = [];

    for (const file of newFiles) {
      const fileUrl = URL.createObjectURL(file);
      const fileId = generateUniqueId();
      filteredFiles.push({ id: fileId, url: fileUrl, file: file } as T);
    }

    let updatedFiles = filteredFiles;

    if (selectedFile !== null && selectedFile >= DEFAULT_NUMBER_ZERO) {
      const newFiles = [...uploadFiles];
      newFiles[selectedFile] = filteredFiles[DEFAULT_NUMBER_ZERO];

      updatedFiles = multiple ? [...newFiles] : filteredFiles;
      setSelectedFile(null);
    } else {
      updatedFiles = multiple ? [...uploadFiles, ...filteredFiles] : filteredFiles;
    }

    if (multiple && updatedFiles?.length > maxFiles) {
      updatedFiles = updatedFiles.slice(DEFAULT_NUMBER_ZERO, maxFiles);
    }

    setUploadFiles(updatedFiles);
    onChange && onChange(updatedFiles);

    if (fileUploadRef.current) {
      fileUploadRef.current.value = EMPTY_STRING;
    }
  };

  const handleRemoveFile = (fileIndex: number) => {
    const newFileList: T[] = uploadFiles.filter((_, index) => index !== fileIndex);

    onChange && onChange(newFileList);
    setUploadFiles(newFileList);
  };

  const renderIconFile = (value: string): string => {
    const fileName = value?.split('.')?.pop();
    switch (fileName) {
      case FileTypeEnum.DOC:
      case FileTypeEnum.DOCX:
        return icons.commonIconFileDocs;

      case FileTypeEnum.XLS:
      case FileTypeEnum.XLSX:
        return icons.commonIconFileXls;

      case FileTypeEnum.PDF:
        return icons.commonIconFilePdf;

      default:
        return icons.commonIconFileDefault;
    }
  };

  const renderPreviewFileUpload = (file: IFileUpload, index: number) => {
    return (
      <Box
        component={'div'}
        className={cx('previewFile')}
        width={height || fileUploadAreaRef.current?.getBoundingClientRect().height}
        height={height || fileUploadAreaRef.current?.getBoundingClientRect().height}
      >
        {file?.file?.type.startsWith(FILE_UPLOAD_TYPE_STARTS_WITH_IMAGE) ||
        file.url?.match(REG_EXP.IS_IMAGE) ? (
          <Avatar
            src={file?.url}
            alt={t('common_alt_img', { ns: 'common/base' })}
            variant='rounded'
            className={cx('image')}
            onClick={() => handleShowUpload(index)}
          />
        ) : (
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
            component={'div'}
            className={cx('previewFileDefault')}
          >
            <Avatar
              src={renderIconFile(file?.file?.name || EMPTY_STRING)}
              alt={t('common_alt_img', { ns: 'common/base' })}
              variant='rounded'
              className={cx('image', 'imagePreviewDefault')}
              onClick={() => handleShowUpload(index)}
            />
          </Stack>
        )}

        <Button
          variant='contained'
          color='secondary'
          className={cx('deleteFile')}
          onClick={() => handleRemoveFile(index)}
        >
          <img
            src={icons.commonIconClose}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('deleteFileIcon')}
          />
        </Button>
      </Box>
    );
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='fileUploadComponent' className={cx('container')}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Button
        ref={fileUploadAreaRef}
        className={cx('fileUpload', { disable: uploadFiles?.length >= maxFiles })}
        sx={{ width, height }}
        onClick={() => handleShowUpload()}
        // disabled={uploadFiles?.length >= maxFiles}
      >
        <input
          ref={fileUploadRef}
          type='file'
          accept={accept}
          name={name || 'fileUpload'}
          id={name || 'fileUpload'}
          {...restProps}
          className={cx('inputUpload')}
          multiple={multiple}
          onChange={handleFileUploadChange}
        />

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={'10px'}
          flexWrap={'wrap'}
        >
          <Avatar src={icons.commonIconFileUpload} alt={t('common_alt_img', { ns: 'common/base' })} />
          <Typography component={'p'} className={cx('placeholder')}>
            {t('component_file_upload_text_btn')}
            <Typography component={'span'} className={cx('placeholderBrowse')}>
              {t('component_file_upload_browse_btn')}
            </Typography>
          </Typography>
        </Stack>
      </Button>

      {uploadFiles?.length > DEFAULT_NUMBER_ZERO && showPreview && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={'12px'}
          className={cx('previewFileContainer', { multiple: multiple })}
        >
          {uploadFiles?.map((file, index) => (
            <div key={index}>{renderPreviewFileUpload(file, index)}</div>
          ))}
        </Stack>
      )}

      {errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};

export default FileUpload;
